import React, { useMemo, useState } from 'react';
import * as translator from '../../../utils/translator';
import * as http from '../../../utils/http';
import StickyNotification from '../../../components/StickyNotification/StickyNotification';
import AdyenCheckoutForm from '../CheckoutForms/AdyenCheckoutForm';
import type { PaymentServiceOverrides } from '../CheckoutForms/AdyenCheckoutForm';
import StripeCheckoutForm from '../CheckoutForms/StripeCheckoutForm';
import PrimerCheckoutForm from '../CheckoutForms/PrimerCheckoutForm';
import type { AdyenInitData, StripeInitData, PrimerInitData, PaymentContext, PaymentCallbacks, PaymentResult } from '../paymentTypes';
import { isStripeProvider, isPrimerProvider } from '../paymentTypes';

export type RepayPspInitData = {
  currency?: string;
  clientKey?: string;
  environment?: AdyenInitData['environment'] | PrimerInitData['environment'];
  publicKey?: string;
  clientToken?: string; // Primer
  gatewayResult?: {
    paymentMethods?: unknown;
    customerSessionSecret?: string;
    clientToken?: string; // Primer
  };
};

interface RepayCheckoutStateProps {
  country: string;
  language: string;
  token: string;
  onPaid: () => void;
  checkoutData: {
    status: 'pending';
    amount: number;
    currency: string;
    components: Array<{ componentType: string; componentName: string | null; quantity: number; amount: number }>;
    paymentServiceProvider: string;
    pspInitData: RepayPspInitData;
    customerData: { first_name: string; last_name: string; email: string };
    orderAddresses: { country_id: string; city: string; street: string; zip: string; house_number: string; phone: string } | null;
  };
}

const EMPTY_ADDRESS = { country_id: '', city: '', street: '', zip: '', house_number: '', phone: '' };

// Repayment transactions have no age/gender/order-history relevance, and payRepayment's
// return shape ({resultCode, action?, refusalReason?}) is a subset of the customer-checkout
// PaymentResult, so it's adapted here rather than widening the shared type for one caller.
// Primer's fields (primerPaymentId, requiredAction, primerError) are passed through as-is.
type RepayResult = { resultCode: string; action?: unknown; refusalReason?: string } & Pick<PaymentResult, 'primerPaymentId' | 'requiredAction' | 'primerError' | 'claimedProvider'>;

function adaptRepayResult(result: RepayResult): PaymentResult {
  return {
    resultCode: result.resultCode as PaymentResult['resultCode'],
    return_url: '',
    refusalReason: result.refusalReason,
    providerResponse: { action: result.action, status: '', client_secret: '' },
    ...(result.primerPaymentId && { primerPaymentId: result.primerPaymentId }),
    ...(result.requiredAction && { requiredAction: result.requiredAction }),
    ...(result.primerError && { primerError: result.primerError }),
    ...(result.claimedProvider && { claimedProvider: result.claimedProvider }),
  };
}

const RepayCheckoutState: React.FC<RepayCheckoutStateProps> = ({ country, language, token, onPaid, checkoutData }) => {
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    content: '',
    refresh: false,
  });

  const isStripe = isStripeProvider(checkoutData.paymentServiceProvider);
  const isPrimer = isPrimerProvider(checkoutData.paymentServiceProvider);
  const provider = checkoutData.paymentServiceProvider || 'adyen';

  const showErrorNotification = (title: string, content: string) => {
    if (!title && !content) {
      setNotification({ show: false, title: '', content: '', refresh: false });
      return;
    }
    setNotification({ show: true, title, content, refresh: true });
  };

  const paymentContext: PaymentContext = useMemo(() => ({
    hashID: token,
    country,
    language,
    currencyCode: (checkoutData.pspInitData.currency || '').toLowerCase(),
    currency: (checkoutData.pspInitData.currency || '').toLowerCase(),
    orderPrice: checkoutData.amount,
    currencyRate: 1,
    itemTitle: '',
    force3DS: null,
    isRepay: true,
    mailCode: null,
    orderIds: [],
    orderHashIDs: [],
    isCannabisCatalog: false,
    thankyouHash: '',
    customerData: { ...checkoutData.customerData, age: 0, gender: 0, paidOrdersCount: 0 },
    catalog: { mention: '' },
    isOrderOnlyPrescription: 0,
    paymentServiceProvider: checkoutData.paymentServiceProvider as PaymentContext['paymentServiceProvider'],
    sepaPositionVariant: '',
  }), [token, country, language, checkoutData]);

  const callbacks: PaymentCallbacks = useMemo(() => ({
    onPaymentSuccess: () => {},
    onPaymentRefused: () => {},
    onPaymentAction: async () => {},
    onExpressUnavailable: (_reason: string, _isNextDayAvailable: boolean) => {},
    onExpressPrescriptionReviewUnavailable: () => {},
    onError: () => showErrorNotification(
      translator.translate(language, 'default', 'StickyNotification/danger_title'),
      translator.translate(language, 'payment', `${provider}/refusalCode/DEFAULT`),
    ),
    showErrorNotification,
    setPaymentRefused: () => {},
    onOutOfStock: () => {},
    onRefreshPage: () => window.location.reload(),
  }), [language, provider]);

  const serviceOverrides: PaymentServiceOverrides = useMemo(() => ({
    skipSessionValidation: true,
    makePayment: async ({ gatewayState, stripeParams, primerParams }) => {
      if (isPrimer) {
        const body = { paymentMethod: gatewayState.paymentMethod, provider: 'primer', gatewayState, primerParams };
        const response = await http.postToServer(`payment-link/${token}/pay`, body, [http.MIDDLEWARES.CUSTOMER_ID]);
        return adaptRepayResult(response.data);
      }
      const paymentMethod = isStripe
        ? { type: gatewayState.paymentMethod.type, confirmationToken: stripeParams!.confirmationToken, paymentMethodTypes: stripeParams!.paymentMethodTypes }
        : gatewayState.paymentMethod;
      const response = await http.postToServer(`payment-link/${token}/pay`, { paymentMethod }, [http.MIDDLEWARES.CUSTOMER_ID]);
      return adaptRepayResult(response.data);
    },
    submitPaymentDetails: async (data: unknown) => {
      const response = await http.postToServer(`payment-link/${token}/payment-details`, data, [http.MIDDLEWARES.CUSTOMER_ID]);
      return adaptRepayResult(response.data);
    },
    onSuccess: () => onPaid(),
  }), [token, isStripe, isPrimer, onPaid]);

  const adyenInitData: AdyenInitData = useMemo(() => ({
    gatewayResult: checkoutData.pspInitData.gatewayResult,
    clientKey: checkoutData.pspInitData.clientKey ?? '',
    environment: checkoutData.pspInitData.environment as AdyenInitData['environment'],
    isSubscription: false,
    customerData: { ...checkoutData.customerData, age: 0, gender: 0, paidOrdersCount: 0 },
    orderAddresses: checkoutData.orderAddresses || EMPTY_ADDRESS,
    reorderedPaymentMethods: false,
  }), [checkoutData]);

  const stripeInitData: StripeInitData = useMemo(() => ({
    gatewayResult: checkoutData.pspInitData.gatewayResult,
    stripePublicKey: checkoutData.pspInitData.publicKey ?? '',
    customerSessionSecret: checkoutData.pspInitData.gatewayResult?.customerSessionSecret ?? '',
    isSubscription: false,
    customerData: { ...checkoutData.customerData, age: 0, gender: 0, paidOrdersCount: 0 },
    orderAddresses: checkoutData.orderAddresses || EMPTY_ADDRESS,
  }), [checkoutData]);

  const primerInitData: PrimerInitData = useMemo(() => ({
    clientToken: checkoutData.pspInitData.clientToken ?? checkoutData.pspInitData.gatewayResult?.clientToken ?? '',
    environment: checkoutData.pspInitData.environment as PrimerInitData['environment'],
    currency: (checkoutData.pspInitData.currency || '').toLowerCase(),
    paymentMethods: Array.isArray(checkoutData.pspInitData.gatewayResult?.paymentMethods) ? checkoutData.pspInitData.gatewayResult!.paymentMethods as string[] : [],
    isSubscription: false,
    customerData: { ...checkoutData.customerData, age: 0, gender: 0, paidOrdersCount: 0 },
    orderAddresses: checkoutData.orderAddresses || EMPTY_ADDRESS,
  }), [checkoutData]);

  return (
    <div data-testid="repay-checkout-state" className="flex flex-col md:flex-row gap-[24px] max-w-[900px] mx-auto py-[40px] px-[16px]">
      <StickyNotification
        level="error"
        showNotification={notification.show}
        title={notification.title}
        content={notification.content}
        autoFade={true}
        refresh={notification.refresh}
        onShown={(() => setNotification((prev) => ({ ...prev, refresh: false }))) as any}
        onClick={() => setNotification((prev) => ({ ...prev, show: false }))}
      />

      <div className="flex-1">
        {isPrimer ? (
          <PrimerCheckoutForm
            primerInitData={primerInitData}
            paymentContext={paymentContext}
            callbacks={callbacks}
            tracking={{ openedTooltips: new Set(), userClicks: new Set() }}
            serviceOverrides={serviceOverrides}
          />
        ) : isStripe ? (
          <StripeCheckoutForm
            stripeInitData={stripeInitData}
            paymentContext={paymentContext}
            callbacks={callbacks}
            tracking={{ openedTooltips: new Set(), userClicks: new Set() }}
            serviceOverrides={serviceOverrides}
          />
        ) : (
          <AdyenCheckoutForm
            adyenInitData={adyenInitData}
            paymentContext={paymentContext}
            callbacks={callbacks}
            tracking={{ openedTooltips: new Set(), userClicks: new Set() }}
            serviceOverrides={serviceOverrides}
          />
        )}
      </div>

      <div className="w-full md:w-[280px] bg-white rounded-[6px] p-[24px] flex flex-col gap-[12px] font-poppins">
        <ul className="flex flex-col gap-[8px]">
          {checkoutData.components.map((component, index) => (
            <li key={index} className="flex items-center justify-between text-[13px] text-[#656d79]">
              <span>
                {component.componentName ?? translator.translate(language, 'repay', `checkout/${component.componentType}`)}
              </span>
              <span>{component.amount.toFixed(2)} {checkoutData.currency}</span>
            </li>
          ))}
        </ul>
        <div className="flex items-center justify-between text-[14px] font-semibold text-[#0d2c54] border-t border-[#e5e5e5] pt-[12px]">
          <span>{translator.translate(language, 'repay', 'checkout/total')}</span>
          <span>{checkoutData.amount.toFixed(2)} {checkoutData.currency}</span>
        </div>
      </div>
    </div>
  );
};

export default RepayCheckoutState;
